import * as React from "react";
import { FieldSchema, OtherFieldSchema, Schema } from "@generation/core";
import { useFormContext, useWatch } from "react-hook-form";
import { RenderFields } from "./RenderFields";
import { FormSectionContainer } from "./FormSectionContainer";
import "./Form.scss";
import { FlexDirectionFormGroupContextProvider } from "../Context/FlexDirectionFormGroupContextProvider";
import { FormButtons, IFormProps, ResetButton } from "../Interfaces/FormProps";
import { FormPropsContextProvider } from "../Context/FormPropsContextProvider";
import { HintPopupContextProvider } from "../Context/HintPopupContextProvider";
import { withForm } from "../Hoc/withForm";
import { FieldRenderContextProvider } from "../Context/FieldRenderContextProvider";
import { arrayFieldRenderer } from "../render/arrayFieldRenderer";
import { defaultFieldRenderer } from "../render/defaultFieldRenderer";
import { objectFieldRenderer } from "../render/objectFieldRenderer";
import { readItemFieldRenderer } from "../render/readItemFieldRenderer";
import { RenderFormButtons } from "./FormButtons";
import { convertButtonsToList } from "../Utils/convertButtonsToList";
import { renderAddedSchemaFields } from "../Interfaces/renderAddedSchemaFields";

interface IFormWrapper {
  onSetShowButtons: (showButtons: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onGetShowButtons?: (values: any) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (values: any) => void;
  children: React.ReactNode;
  afterFormFieldsContent?: React.ReactNode;
}

export const FormWrapper = (props: IFormWrapper) => {
  const values = useWatch();
  const [showButtons, setShowButtons] = React.useState(!props.onGetShowButtons);

  React.useLayoutEffect(() => {
    if (props.onSetShowButtons) {
      props.onSetShowButtons(showButtons);
    }
  }, [showButtons]);

  React.useLayoutEffect(() => {
    if (props.onGetShowButtons) {
      setShowButtons(props.onGetShowButtons(values));
    }
    if (props.onChange) {
      props.onChange(values);
    }
  }, [values]);

  return <>{props.children}</>;
};

export interface FormProps extends IFormProps<Schema> {}

export const Form = withForm(
  React.memo((props: FormProps) => {
    const form = useFormContext();
    const [showButtons, setShowButtons] = React.useState(true);

    const onSetShowButtons = (value: boolean) => {
      setShowButtons(value);
    };

    const buttons = convertButtonsToList(props);

    return (
      <FormPropsContextProvider value={props}>
        <FieldRenderContextProvider
          value={{
            array: arrayFieldRenderer,
            default: defaultFieldRenderer,
            object: objectFieldRenderer,
            readItem: readItemFieldRenderer
          }}
        >
          <HintPopupContextProvider>
            <FlexDirectionFormGroupContextProvider value={props.uiDefaults?.flexDirectionFormGroupControl}>
              <FormWrapper
                onChange={props.onChange}
                onGetShowButtons={props.onShowButtons}
                onSetShowButtons={onSetShowButtons}
              >
                <form
                  autoComplete="off"
                  onReset={(e) => {
                    form.reset(props.formOptions?.defaultValues, {
                      keepErrors: false,
                      keepDirty: false,
                      keepIsSubmitted: false,
                      keepTouched: false,
                      keepIsValid: false,
                      keepSubmitCount: false
                    });

                    const isResetButton = (button: FormButtons): button is ResetButton => {
                      return button.type === "reset";
                    };

                    const button: ResetButton | undefined = buttons.find(isResetButton);

                    if (button?.handle) {
                      button.handle(e);
                    }
                  }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    return false;
                  }}
                >
                  <div className={props.hide !== true ? "show" : "hide"}>
                    <RenderForm
                      schema={props.schema}
                      renderAddedSchemaFields={props.renderAddedSchemaFields}
                    />
                    {props.afterFormFieldsContent}
                    {showButtons && <RenderFormButtons buttons={buttons} implementClickHandlers={true} />}
                  </div>
                </form>
              </FormWrapper>
            </FlexDirectionFormGroupContextProvider>
          </HintPopupContextProvider>
        </FieldRenderContextProvider>
      </FormPropsContextProvider>
    );
  })
);

export const RenderForm = React.memo((props: Pick<FormProps, "schema" | "renderAddedSchemaFields">) => (
  <>
    {Object.entries(props.schema).map((sectie) => (
      <RenderFormSection
        key={`${sectie[0]}`}
        field={sectie[1]}
        name={sectie[0]}
        renderAddedSchemaFields={props.renderAddedSchemaFields}
      />
    ))}
  </>
));

export const RenderFormSection = React.memo(
  (props: { name: string; field: FieldSchema; renderAddedSchemaFields?: renderAddedSchemaFields }) => (
    <FormSectionContainer field={props.field} name={props.name}>
      <RenderFields
        field={props.field}
        name={props.name}
        renderAddedSchemaFields={props.renderAddedSchemaFields}
      />
    </FormSectionContainer>
  )
);
